import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setErrors } from 'redux/slices/systemSlice';
import { authService } from 'spec';
import { GET_STATE } from '../constants/aryGraphqlSchemas';
import useTemplateParsing from './useTemplateParsing';

const REQUEST_TIME_OUT_TEMPLATE_ID = 'Request-Time-Out';
const REQUEST_NETWORK_TIME_OUT_TEMPLATE_ID = 'Request-Network-Time-Out';
const TRANSACTION_TIME_OUT_DURATION = 7000;

const useAryGetState = ({
  stateId,
  type,
  onPending,
  onFailed,
  onSucceed,
  intervalTime = 1000,
  eachrequestTimeOut = 3000
}) => {
  const dispatch = useDispatch();
  const [accessToken, setAccessToken] = useState(null);
  const getStateIntervalId = useRef(null);
  const getTimeoutId = useRef(null);
  const requestStatus = useRef(null);
  const allRequests = useRef(new Map());
  const [requestCount, setRequestCount] = useState(0);
  const hostname = window.paths.bhn;
  const parseDescription = useTemplateParsing();

  useEffect(() => {
    const fetchAccessToken = async () => {
      const token = await authService.getAccessToken();
      setAccessToken(token);
    };
    fetchAccessToken();
  }, []);

  const clearAllTimers = () => {
    if (getStateIntervalId.current) {
      clearInterval(getStateIntervalId.current);
      getStateIntervalId.current = null;
    }
    if (getTimeoutId.current) {
      clearTimeout(getTimeoutId.current);
      getTimeoutId.current = null;
    }
    allRequests.current.forEach(({ timeoutId, controller }) => {
      clearTimeout(timeoutId);
      controller?.abort();
    });
    allRequests.current.clear();
  };

  const GET_STATE_QUERY = `
    query GetState($stateStatus: SagaStateRequest) {
      getStateGroupQuery {
        getState(stateStatus: $stateStatus) {
          status
          metadata {
            hook
            value
          }
          templateId
        }
      }
    }`;

  const fetchState = async (requestId) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      dispatch(
        setErrors({
          templateId: REQUEST_NETWORK_TIME_OUT_TEMPLATE_ID,
          metadata: []
        })
      );
      clearAllTimers();
    }, eachrequestTimeOut);

    allRequests.current.set(requestId, { timeoutId, controller });
    setRequestCount(allRequests.current.size);
    try {
      const response = await fetch(`${hostname}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          query: GET_STATE_QUERY,
          variables: {
            stateStatus: { stateId, type }
          }
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      allRequests.current.delete(requestId);
      if (error.name !== 'AbortError') {
        dispatch(
          setErrors({
            templateId: REQUEST_NETWORK_TIME_OUT_TEMPLATE_ID,
            metadata: []
          })
        );
        clearAllTimers();
      }
      throw error;
    }
  };
  useEffect(() => {
    if (requestCount >= Number(TRANSACTION_TIME_OUT_DURATION / intervalTime)) {
      dispatch(
        setErrors({
          templateId: REQUEST_TIME_OUT_TEMPLATE_ID,
          metadata: []
        })
      );
      if (onFailed) {
        onFailed();
      }
      clearAllTimers();
    }
  }, [requestCount]);
  useEffect(() => {
    if (!stateId || !accessToken) return;
    requestStatus.current = GET_STATE.status.PENDING;

    getStateIntervalId.current = setInterval(async () => {
      if (requestStatus.current === GET_STATE.status.SUCCEED) {
        clearAllTimers();
        return;
      }

      const requestId = Date.now();
      try {
        const resp = await fetchState(requestId);
        const stateStatus = resp.data?.getStateGroupQuery?.getState?.status;
        const templateId = resp.data?.getStateGroupQuery?.getState?.templateId;
        const metadata = resp.data?.getStateGroupQuery?.getState?.metadata;
        requestStatus.current = stateStatus;
        let parsedMessage;
        if (templateId && metadata) {
          parsedMessage = parseDescription(templateId, metadata);
        }
        if (stateStatus === GET_STATE.status.FAILED) {
          clearAllTimers();
          dispatch(setErrors({ templateId, metadata }));
          if (onFailed) {
            onFailed();
          }
        } else if (stateStatus === GET_STATE.status.SUCCEED) {
          clearAllTimers();
          if (onSucceed) {
            onSucceed(parsedMessage);
          }
        } else if (stateStatus === GET_STATE.status.PENDING) {
          if (onPending) {
            onPending(parsedMessage);
          }
        }
      } catch (error) {
        // Error handling is done in fetchState
      }
    }, intervalTime);
  }, [stateId, accessToken]);

  return { status: requestStatus.current };
};

export default useAryGetState;
